import { useEffect, useState } from 'react';
import { useApiClient } from '@rudi/api/react';
import { LiveViewError, openLiveSession, type LiveSession } from './session';

export type LiveSessionState =
  | { phase: 'opening'; waiting: boolean }
  | { phase: 'ready'; session: LiveSession }
  /** `error` is null when the operator closed the view. */
  | { phase: 'ended'; error: LiveViewError | null };

/**
 * Opens a live view session while the calling page is mounted and closes it
 * when the page goes away or the tab is hidden for good. Change `attempt` to
 * open a new session after one ended.
 */
export function useLiveSession(
  input: { deviceId: string; runId?: string },
  attempt = 0
): LiveSessionState {
  const api = useApiClient();
  const { deviceId, runId } = input;
  const [state, setState] = useState<LiveSessionState>({ phase: 'opening', waiting: false });

  useEffect(() => {
    const controller = new AbortController();
    let session: LiveSession | null = null;
    let active = true;
    setState({ phase: 'opening', waiting: false });

    openLiveSession(
      api,
      { deviceId, runId },
      {
        signal: controller.signal,
        onWaiting: () => active && setState({ phase: 'opening', waiting: true }),
        onEnd: error => active && setState({ phase: 'ended', error }),
      }
    ).then(
      opened => {
        if (!active) return void opened.close();
        session = opened;
        setState({ phase: 'ready', session: opened });
      },
      (error: unknown) => {
        if (!active) return;
        setState({
          phase: 'ended',
          error:
            error instanceof LiveViewError
              ? error
              : new LiveViewError('failed', 'The live view could not open.'),
        });
      }
    );

    // A closed tab never unmounts, so try to close on pagehide as well.
    const leave = () => void session?.close();
    window.addEventListener('pagehide', leave);
    return () => {
      active = false;
      window.removeEventListener('pagehide', leave);
      controller.abort();
      void session?.close();
    };
  }, [api, deviceId, runId, attempt]);

  return state;
}
